import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useWorkspace } from 'hooks/useWorkspace';

import styles from 'Pages/TeamDashboard/ProductLibrary/styles.module.scss';

import actionTypes from 'Redux/constants';

import { getFlowDetail } from 'services/productLibrary';

import CategoryNav from './CategoryNav';
import FlowSection from './FlowSection';

const FilledState = ({
  categories,
  onAddFlow,
  selectionMode,
  selectedLibraryFlowUuid,
  onLibraryFlowSelect,
  screenCount,
  onAddVersion,
  onVersionSelectExternal,
  selectedVersionsExternal,
}) => {
  const dispatch = useDispatch();
  const { selectedWorkspace, isOwner } = useWorkspace();

  const [selectedFlowUuid, setSelectedFlowUuid] = useState(null);
  const [openVersionDropdownUuid, setOpenVersionDropdownUuid] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(
    () => new Set(categories.map(c => c.uuid))
  );
  const [internalSelectedVersions, setInternalSelectedVersions] = useState({});

  const flowRefs = useRef({});
  const flowsPanelRef = useRef(null);

  // Use external version state if provided (selection mode), otherwise use internal
  const selectedVersions = useMemo(
    () => (selectionMode && selectedVersionsExternal ? selectedVersionsExternal : internalSelectedVersions),
    [selectionMode, selectedVersionsExternal, internalSelectedVersions]
  );

  // Expand any newly-added categories without collapsing existing ones
  useEffect(() => {
    setExpandedCategories(prev => {
      const additions = categories.filter(c => !prev.has(c.uuid));

      if (additions.length === 0) {
        return prev;
      }

      const next = new Set(prev);

      additions.forEach(c => next.add(c.uuid));

      return next;
    });
  }, [categories]);

  const allFlows = useMemo(() => categories.flatMap(c => c.flows), [categories]);

  // Highlight the first visible flow section as the user scrolls
  useEffect(() => {
    const panelEl = flowsPanelRef.current;

    if (!panelEl) {
      return;
    }

    const visibleUuids = new Set();

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const { flowUuid } = entry.target.dataset;

          if (entry.isIntersecting) {
            visibleUuids.add(flowUuid);
          }
          else {
            visibleUuids.delete(flowUuid);
          }
        });

        const firstVisible = allFlows.find(f => visibleUuids.has(f.uuid));

        if (firstVisible) {
          setSelectedFlowUuid(firstVisible.uuid);
        }
      },
      { root: panelEl, threshold: 0.1 }
    );

    allFlows.forEach(flow => {
      const el = flowRefs.current[flow.uuid];

      if (el) {
        el.dataset.flowUuid = flow.uuid;
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, [allFlows]);

  const handleFlowSelect = useCallback(flowUuid => {
    setSelectedFlowUuid(flowUuid);
    const el = flowRefs.current[flowUuid];

    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    if (selectionMode && onLibraryFlowSelect) {
      onLibraryFlowSelect(flowUuid);
    }
  }, [selectionMode, onLibraryFlowSelect]);

  const handleCategoryToggle = useCallback(categoryUuid => {
    setExpandedCategories(prev => {
      const next = new Set(prev);

      if (next.has(categoryUuid)) {
        next.delete(categoryUuid);
      }
      else {
        next.add(categoryUuid);
      }

      return next;
    });
  }, []);

  const handleVersionDropdownToggle = useCallback(flowUuid => {
    setOpenVersionDropdownUuid(prev => prev === flowUuid ? null : flowUuid);
  }, []);

  const handleVersionDropdownClose = useCallback(() => {
    setOpenVersionDropdownUuid(null);
  }, []);

  const handleVersionSelect = useCallback(async (flowUuid, versionNumber) => {
    // Store the selected version for this flow
    if (selectionMode && onVersionSelectExternal) {
      onVersionSelectExternal(flowUuid, versionNumber);
    }
    else {
      setInternalSelectedVersions(prev => ({
        ...prev,
        [flowUuid]: versionNumber,
      }));
    }

    // Fetch the flow detail for the selected version
    try {
      const response = await getFlowDetail(selectedWorkspace?.uuid, flowUuid, versionNumber);
      const flowData = response.flow;

      // Update the Redux store with the version data
      dispatch({
        type: actionTypes.productLibrary.updateFlowVersion,
        payload: {
          flowUuid,
          versionData: {
            assets: flowData.assets,
            screen_count: flowData.screen_count,
            version_number: versionNumber,
          },
        },
      });
    }
    catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch flow version:', error);
    }

    // Close the dropdown
    setOpenVersionDropdownUuid(null);
  }, [dispatch, onVersionSelectExternal, selectedWorkspace?.uuid, selectionMode]);

  return (
    <div className={styles.filledLayout}>
      <CategoryNav
        categories={categories}
        selectedFlowUuid={selectedFlowUuid}
        expandedCategories={expandedCategories}
        onFlowSelect={handleFlowSelect}
        onCategoryToggle={handleCategoryToggle}
        onAddFlow={isOwner ? onAddFlow : null}
        selectionMode={selectionMode}
        selectedLibraryFlowUuid={selectedLibraryFlowUuid}
        screenCount={screenCount}
      />
      <div ref={flowsPanelRef} className={styles.flowsPanel}>
        {allFlows.map(flow => (
          <FlowSection
            key={flow.uuid}
            flow={flow}
            sectionRef={el => {
              flowRefs.current[flow.uuid] = el;
            }}
            isVersionDropdownOpen={openVersionDropdownUuid === flow.uuid}
            onVersionDropdownToggle={() => handleVersionDropdownToggle(flow.uuid)}
            onVersionDropdownClose={handleVersionDropdownClose}
            isLibrarySelected={selectionMode && selectedLibraryFlowUuid === flow.uuid}
            onAddVersion={onAddVersion}
            onVersionSelect={handleVersionSelect}
            selectedVersion={selectedVersions[flow.uuid]}
            showAddVersion={!selectionMode && isOwner}
          />
        ))}
      </div>
    </div>
  );
};

FilledState.propTypes = {
  categories: PropTypes.array.isRequired,
  onAddFlow: PropTypes.func,
  selectionMode: PropTypes.bool,
  selectedLibraryFlowUuid: PropTypes.string,
  onLibraryFlowSelect: PropTypes.func,
  screenCount: PropTypes.number,
  onAddVersion: PropTypes.func,
  onVersionSelectExternal: PropTypes.func,
  selectedVersionsExternal: PropTypes.object,
};

FilledState.defaultProps = {
  onAddFlow: null,
  selectionMode: false,
  selectedLibraryFlowUuid: null,
  onLibraryFlowSelect: null,
  screenCount: null,
  onAddVersion: null,
  onVersionSelectExternal: null,
  selectedVersionsExternal: null,
};

export default FilledState;
